import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";
import { findAsset } from "@/data/content";
import { pickLocale } from "@/i18n";

function gridColsForCount(n) {
  if (n === 5) return "sm:grid-cols-2 lg:grid-cols-5";
  if (n === 6) return "sm:grid-cols-2 lg:grid-cols-3";
  return "sm:grid-cols-2 lg:grid-cols-4";
}

// Map English key data label → i18n key so we localise field labels
function localiseKey(t, key) {
  const map = {
    "Location": t("investments.labels.location"),
    "Project Type": t("investments.labels.projectType"),
    "Asset Type": t("investments.labels.assetType"),
    "Scale": t("investments.labels.scale"),
    "Open Space": t("investments.labels.openSpace"),
    "Status": t("investments.labels.status"),
    "Master Plan": t("investments.labels.masterPlan"),
    "Standards": t("investments.labels.standards"),
    "Data": t("investments.labels.data"),
  };
  return map[key] || key;
}

export default function AssetDetail() {
  useReveal();
  const { t } = useTranslation();
  const { slug } = useParams();
  const asset = findAsset(slug);

  if (!asset) {
    return (
      <div className="min-h-screen bg-white text-unik-ink">
        <SiteNav />
        <div className="mx-auto max-w-[1200px] px-6 py-40 text-center">
          <h1 className="font-display text-4xl text-[#14355a]">Asset not found</h1>
          <Link to="/investments" className="link-rule mt-8 inline-block text-[#14355a] text-sm">
            {t("assetDetail.backToInvestments")}
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const localizedKeyData = pickLocale(asset, "keyData") || asset.keyData;
  const visibleKeyData = localizedKeyData.filter(
    (d) => !/investment|tổng đầu tư/i.test(d.k)
  );
  const displayUnit = pickLocale(asset, "unit") || asset.unit;

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      {/* Hero */}
      <section className="relative h-[70vh] lg:h-[80vh] overflow-hidden bg-[#14355a]">
        <img src={asset.img} alt={pickLocale(asset, "name") || asset.name} className="absolute inset-0 w-full h-full object-cover opacity-65" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e2540] via-[#0e2540]/55 to-[#14355a]/40" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 h-full flex flex-col justify-end pb-16 lg:pb-24 text-white">
          <Link to="/investments" data-reveal className="inline-flex items-center gap-2 text-white/80 text-xs uppercase tracking-[0.2em] font-display hover:text-unik-gold transition-colors mb-8">
            <ArrowLeft size={14} /> {t("assetDetail.backToInvestments")}
          </Link>
          <div data-reveal className="eyebrow text-unik-gold">{pickLocale(asset, "type") || asset.type}</div>
          <h1
            data-reveal
            style={{ "--reveal-delay": "120ms", fontSize: "clamp(2.5rem, 6vw, 5.5rem)", fontWeight: 500, lineHeight: "1.0" }}
            className="mt-6 font-display tracking-tight max-w-4xl"
          >
            {pickLocale(asset, "name") || asset.name}
          </h1>
          <p data-reveal style={{ "--reveal-delay": "240ms" }} className="mt-6 text-white/80 text-base lg:text-lg">
            {pickLocale(asset, "location") || asset.location}
          </p>
        </div>
      </section>

      {/* Key Data */}
      <section className="relative bg-unik-cream py-20 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div data-reveal className="mb-12 flex items-end justify-between gap-8 flex-wrap">
            <div>
              <div className="eyebrow text-[#14355a]">{t("investments.labels.keyData")}</div>
              <div className="mt-6 w-12 h-px bg-unik-gold" />
            </div>
            <div className="text-right">
              <div className="font-display text-[#14355a]" style={{ fontSize: "clamp(2rem, 3vw, 2.5rem)", fontWeight: 500, lineHeight: 1 }}>
                {asset.metric}
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[#14355a]/60 font-medium">
                {displayUnit}
              </div>
            </div>
          </div>
          <div
            className={`grid gap-px bg-[#14355a]/10 ${gridColsForCount(visibleKeyData.length)}`}
          >
            {visibleKeyData.map((d, i) => (
              <div
                key={d.k}
                data-reveal
                style={{ "--reveal-delay": `${i * 70}ms` }}
                className="bg-white p-7 lg:p-9 flex flex-col"
              >
                <div className="text-[11px] lg:text-[12px] uppercase tracking-[0.22em] text-unik-ink/60 font-medium">
                  {localiseKey(t, d.k)}
                </div>
                <div className="mt-4 font-display text-[#14355a] text-[17px] lg:text-[19px] tracking-tight leading-[1.5]" style={{ fontWeight: 500 }}>
                  {d.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="relative bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-20">
          <div className="lg:col-span-5" data-reveal>
            <div className="eyebrow text-[#14355a]">{t("assetDetail.overviewEyebrow")}</div>
            <h2
              style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)", fontWeight: 500, lineHeight: "1.0" }}
              className="mt-8 font-display text-[#14355a] tracking-tight"
            >
              {t("assetDetail.overviewTitle")} {t("assetDetail.overviewAccent")}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[16px] lg:text-[17px] leading-[1.95] text-unik-ink/80">
            {(pickLocale(asset, "body") || asset.body).map((p, i) => (
              <p key={p.slice(0, 32)} data-reveal style={{ "--reveal-delay": `${i * 100}ms` }}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      <BottomCTA
        eyebrow={t("investments.bottomCta.eyebrow")}
        title={t("investments.bottomCta.title")}
        accent={t("investments.bottomCta.accent")}
        body={t("investments.bottomCta.body")}
        primary={{ label: t("investments.bottomCta.primary"), to: "/contact" }}
        secondary={{ label: t("investments.subnav.overview"), to: "/investments" }}
      />
      <SiteFooter />
    </div>
  );
}
